/**
 * Cross-component handoff for "start a thread with this user message"
 * requests originating outside the thread sidebar (e.g. an "Agent
 * builder" CTA on the agents list page).
 *
 * The producer calls `stashPendingKickoff` which writes the message to
 * sessionStorage AND dispatches `PENDING_KICKOFF_EVENT`. The consumer
 * calls `subscribePendingKickoff` which owns both channels: on
 * subscribe, it reads any latched message; while subscribed, it
 * listens for the window event. Both arrive through one handler so
 * callers don't have to dedupe — the storage stash exists solely to
 * bridge the mount-after-stash case where the event would otherwise be
 * missed.
 *
 * Producer-side contract: this module assumes a single producer per
 * page session. Two rapid `stashPendingKickoff` calls dispatch two
 * synchronous events, which a mounted subscriber will run back-to-back
 * (no coalescing). If a future caller needs to coalesce, do it at the
 * caller — by the time the second stash hits, the first thread has
 * already started and dedup here would race the in-flight mutation.
 */

const PENDING_KICKOFF_STORAGE_KEY = "roboto.pending-agentThread-kickoff";

/** Custom DOM event dispatched on the window when a kickoff is stashed. */
export const PENDING_KICKOFF_EVENT = "roboto:pending-agentThread-kickoff";

export interface PendingKickoffEventDetail {
  message: string;
}

/**
 * Stash a kickoff message and announce it to any mounted subscribers.
 *
 * Single-producer contract: see module docstring. Stashing twice in
 * quick succession will fire two events back-to-back — the consumer
 * does not coalesce.
 *
 * Silently no-ops the storage write if sessionStorage is unwritable;
 * the event-channel path still fires for any currently-subscribed
 * listener, so a thread sidebar that's already open still receives the
 * kickoff. The latch-on-mount path (subscribers that register *after*
 * the stash) is what's lost in that branch.
 */
export function stashPendingKickoff(message: string): void {
  try {
    sessionStorage.setItem(PENDING_KICKOFF_STORAGE_KEY, message);
  } catch {
    // Storage unavailable — see docstring.
  }
  window.dispatchEvent(
    new CustomEvent<PendingKickoffEventDetail>(PENDING_KICKOFF_EVENT, {
      detail: { message },
    }),
  );
}

/**
 * Read and clear the pending kickoff. Returns `null` when no kickoff
 * is pending. Destructive — a second call returns `null` even if the
 * first call returned a message. Most callers should use
 * `subscribePendingKickoff` instead; this is exposed for tests and for
 * the cleanup that `subscribePendingKickoff`'s window-event listener
 * performs internally.
 */
export function consumePendingKickoff(): string | null {
  let value: string | null;
  try {
    value = sessionStorage.getItem(PENDING_KICKOFF_STORAGE_KEY);
  } catch {
    return null;
  }
  if (value === null) {
    return null;
  }
  try {
    sessionStorage.removeItem(PENDING_KICKOFF_STORAGE_KEY);
  } catch {
    // Best-effort clear.
  }
  return value;
}

/**
 * Subscribe to pending kickoffs, joining both the storage latch and
 * the window event into a single callback channel.
 *
 * On subscribe, immediately calls `handler` with any message that was
 * stashed before the subscription was registered (the
 * mount-after-stash case). While subscribed, calls `handler` for every
 * `PENDING_KICKOFF_EVENT` the window receives, draining the storage
 * stash in between so the latch and the event channel can't
 * double-fire for the same message.
 *
 * Returns the cleanup function the caller should invoke on unmount —
 * shaped to match `React.useEffect`'s teardown contract.
 *
 * The handler may close over stale React state if the caller stores it
 * directly. Use the latest-ref pattern (`React.useRef` + reassign in
 * an effect) at the call site to forward each invocation to the most
 * recent closure.
 */
export function subscribePendingKickoff(
  handler: (message: string) => void,
): () => void {
  const initial = consumePendingKickoff();
  if (initial !== null) {
    handler(initial);
  }
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<PendingKickoffEventDetail>).detail;
    // Drain the latch so a later mount-after-stash doesn't replay this
    // same message.
    consumePendingKickoff();
    handler(detail.message);
  };
  window.addEventListener(PENDING_KICKOFF_EVENT, listener);
  return () => {
    window.removeEventListener(PENDING_KICKOFF_EVENT, listener);
  };
}

/**
 * Internal hook for tests — exported so tests can reset state between
 * cases without poking at the storage key directly.
 */
export const __PENDING_KICKOFF_STORAGE_KEY_FOR_TESTS =
  PENDING_KICKOFF_STORAGE_KEY;
